import React, { useEffect } from "react";
import {
  Menu,
  User,
  Settings,
  LogOut,
  Globe,
  DollarSign,
  Palette,
  Camera,
  Star,
  Scan,
  ShoppingBag,
  Newspaper,
  Radar,
  Search,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import {
  useSettings,
  Theme,
  Currency,
  Language,
} from "@/contexts/SettingsContext";
import { useNavigate } from "react-router";
import { useIsMobile } from "@/hooks/use-mobile";

interface UserMenuProps {
  onAuthClick: (mode?: "login" | "signup") => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ onAuthClick }) => {
  const { user, signOut } = useAuth();
  const {
    theme,
    currency,
    language,
    avatar,
    setTheme,
    setCurrency,
    setLanguage,
    setAvatar,
  } = useSettings();

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    if (theme === "dark") {
      root.classList.add("dark");
    } else if (theme === "light") {
      root.classList.remove("dark");
    } else {
      // System theme
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      if (prefersDark) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    }
  }, [theme]);

  const handleSignOut = async () => {
    await signOut();
  };

  const themeOptions: { value: Theme; label: string }[] = [
    { value: "light", label: "Light" },
    { value: "dark", label: "Dark" },
    { value: "system", label: "System" },
  ];

  const avatarOptions = [
    "🦙",
    "🐱",
    "🐶",
    "🦊",
    "🐻",
    "🐼",
    "🐨",
    "🐯",
    "🦁",
    "🐸",
  ];

  return (
    <div className="flex items-center space-x-3">
      {/* Login button and signup link when not logged in */}
      {!user && (
        <div className="flex items-center justify-center text-center">
          <Button
            onClick={() => onAuthClick("login")}
            className="bg-gradient-to-r from-holo-blue to-electric-yellow hover:from-holo-blue/90 hover:to-electric-yellow/90 text-deep-indigo font-bold font-inter px-4"
            size="sm"
          >
            Log In
          </Button>
        </div>
      )}

      {/* Hamburger Menu - Always visible */}
      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="p-2 text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-lg z-50 transition-colors duration-300"
            align="end"
            sideOffset={8}
          >
            {/* Theme Setting */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                <Palette className="w-4 h-4 mr-2" />
                Theme
                <span className="ml-auto text-gray-500 dark:text-gray-400 capitalize">
                  {theme}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-lg">
                <DropdownMenuRadioGroup
                  value={theme}
                  onValueChange={(value) => setTheme(value as Theme)}
                >
                  {themeOptions.map((themeOption) => (
                    <DropdownMenuRadioItem
                      key={themeOption.value}
                      value={themeOption.value}
                      className="text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {themeOption.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            {/* User-specific options when logged in */}
            {user && (
              <>
                <DropdownMenuItem
                  className="flex items-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => navigate("/market")}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Market
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="flex items-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => navigate("/virtual-binder")}
                >
                  <User className="w-4 h-4 mr-2" />
                  Binder
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="flex items-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => navigate("/news")}
                >
                  <Newspaper className="w-4 h-4 mr-2" />
                  News
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="flex items-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => navigate("/cards")}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="flex items-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => navigate("/deal-hunter")}
                >
                  <Radar className="w-4 h-4 mr-2" />
                  Deal Hunter
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="flex items-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => navigate("/watchlist")}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Watchlist
                </DropdownMenuItem>

                {isMobile && (
                  <DropdownMenuItem
                    className="flex items-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => navigate("/scan")}
                  >
                    <Scan className="w-4 h-4 mr-2" />
                    Scan
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="flex items-center text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Avatar - shown when logged in */}
      {user && (
        <Avatar className="w-8 h-8">
          <AvatarImage src="" />
          <AvatarFallback className="bg-gradient-to-br from-holo-blue to-deep-indigo text-white">
            {avatar ||
              user.user_metadata?.full_name?.[0] ||
              user.email?.[0] ||
              "U"}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};

export default UserMenu;
